import { useState, useMemo, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import {
    Plus,
    AlertCircle,
    AlertTriangle,
    Circle,
    Calendar,
    Search,
    Filter,
    X,
    CalendarDays,
    ClipboardList,
    Archive
} from 'lucide-react';
import { Ticket as TicketType } from '../../types';
import { api } from '../../api/client';

type TabType = 'my' | 'department' | 'all';

export default function TicketList() {
    const { tickets, setActiveView, setShowCreateTicket, currentUser, users, departments, settings, isAdmin: adminFlag } = useApp();
    const userRole = currentUser?.role?.name?.toLowerCase() || '';
    const isAdmin = adminFlag || (typeof userRole === 'string' && userRole.includes('admin'));
    const userDepartmentIds = currentUser?.department_ids || (currentUser?.department_id ? [currentUser.department_id] : []);

    // Tab state - default to "my" tickets
    const [activeTab, setActiveTab] = useState<TabType>('my');

    // Filter states
    const [statusFilter, setStatusFilter] = useState<string>('open'); // Default to open (hide closed)
    const [priorityFilter, setPriorityFilter] = useState<string>('all');
    const [departmentFilter, setDepartmentFilter] = useState<string>('all');
    const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
    const [tagFilter, setTagFilter] = useState<string>('all');

    // Search states
    const [ticketNumberSearch, setTicketNumberSearch] = useState('');
    const [generalSearch, setGeneralSearch] = useState('');

    // UI states
    const [showFilters, setShowFilters] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [deletedTickets, setDeletedTickets] = useState<TicketType[]>([]);

    const canViewDeleted = isAdmin || (currentUser?.role?.permissions || []).includes('tickets:view_deleted');

    useEffect(() => {
        if (showDeleted && canViewDeleted) {
            api.getTickets({ deleted: 'true' })
                .then(data => {
                    if (Array.isArray(data)) {
                        setDeletedTickets(data);
                    } else {
                        console.error('Expected array from getTickets, got:', data);
                        setDeletedTickets([]);
                    }
                })
                .catch(err => {
                    console.error('Failed to fetch deleted tickets:', err);
                    setDeletedTickets([]);
                });
        }
    }, [showDeleted, canViewDeleted]);

    // Check if user is department head
    const isDepartmentHead = useMemo(() => {
        return departments.some(dept => dept.head_ids?.includes(currentUser?.id || ''));
    }, [departments, currentUser?.id]);

    // Available tabs based on role
    const availableTabs = useMemo(() => {
        const tabs: { id: TabType; label: string }[] = [
            { id: 'my', label: 'My Tickets' }
        ];
        if (isDepartmentHead || isAdmin) {
            tabs.push({ id: 'department', label: 'Department' });
        }
        if (isAdmin) {
            tabs.push({ id: 'all', label: 'All Tickets' });
        }
        return tabs;
    }, [isDepartmentHead, isAdmin]);

    // Filter tickets based on tab and filters
    const filteredTickets = useMemo(() => {
        let result = (showDeleted ? deletedTickets : [...tickets]) || [];
        // Ensure result is an array before filtering
        if (!Array.isArray(result)) {
            console.error('Tickets result is not an array:', result);
            result = [];
        }

        // Ensure we filter out deleted tickets if we're not showing them
        if (!showDeleted) {
            result = result.filter(t => t && !t.deleted_at && t.status !== 'deleted');
        }

        const userId = currentUser?.id;

        // Tab-based filtering
        if (activeTab === 'my') {
            result = result.filter(t =>
                t.created_by === userId ||
                t.assignees?.some(a => a.id === userId) ||
                t.collaborators?.some(c => c.id === userId)
            );
        } else if (activeTab === 'department') {
            // Get departments the user heads
            const headedDeptIds = departments
                .filter(d => d.head_ids?.includes(userId || ''))
                .map(d => d.id);
            // Also include user's own departments
            const allDeptIds = [...new Set([...headedDeptIds, ...userDepartmentIds])];
            result = result.filter(t =>
                allDeptIds.includes(t.department_id || '') ||
                allDeptIds.includes(t.assigned_to_department_id || '')
            );
        }
        // 'all' tab shows everything (admin only)

        // Status filter (ignore for deleted view)
        if (statusFilter !== 'all' && !showDeleted) {
            result = result.filter(t => t.status === statusFilter);
        }

        // Priority filter
        if (priorityFilter !== 'all') {
            result = result.filter(t => t.priority === priorityFilter);
        }

        // Department filter
        if (departmentFilter !== 'all') {
            result = result.filter(t => t.department_id === departmentFilter);
        }

        // Assignee filter
        if (assigneeFilter !== 'all') {
            result = result.filter(t => t.assignees?.some(a => a.id === assigneeFilter));
        }

        // Tag filter
        if (tagFilter !== 'all') {
            result = result.filter(t => t.tags?.includes(tagFilter));
        }

        // Ticket number search
        if (ticketNumberSearch) {
            const searchNum = parseInt(ticketNumberSearch.replace('#', ''), 10);
            result = result.filter(t => t.ticket_number === searchNum);
        }

        // General search
        if (generalSearch) {
            const search = generalSearch.toLowerCase();
            result = result.filter(t =>
                t.title.toLowerCase().includes(search) ||
                t.description?.toLowerCase().includes(search)
            );
        }

        // Sort: assigned first, then collaborating, then by date
        result.sort((a, b) => {
            const aAssigned = a.assignees?.some(u => u.id === userId) ? 1 : 0;
            const bAssigned = b.assignees?.some(u => u.id === userId) ? 1 : 0;
            if (aAssigned !== bAssigned) return bAssigned - aAssigned;

            // Then by date (newest first)
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        return result;
    }, [tickets, activeTab, statusFilter, priorityFilter, departmentFilter, assigneeFilter, tagFilter, ticketNumberSearch, generalSearch, currentUser?.id, departments, userDepartmentIds]);

    // Helpers
    const getInitials = (name: string) => {
        return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const getPriorityIcon = (priority: string) => {
        const props = { size: 14, strokeWidth: 2 };
        switch (priority) {
            case 'urgent': return <AlertTriangle {...props} color="var(--accent-danger)" />;
            case 'high': return <AlertCircle {...props} color="var(--color-warning)" />;
            case 'medium': return <Circle {...props} color="var(--accent-info)" />;
            case 'low': return <Circle {...props} color="var(--text-muted)" opacity={0.5} />;
            default: return null;
        }
    };

    const isAssignedToMe = (ticket: TicketType) => ticket.assignees?.some(a => a.id === currentUser?.id);
    const isCollaborator = (ticket: TicketType) => ticket.collaborators?.some(c => c.id === currentUser?.id);
    const isOverdue = (ticket: TicketType) => ticket.due_date && new Date(ticket.due_date) < new Date() && ticket.status !== 'closed';

    const availableTags = settings?.available_tags || [];

    const clearFilters = () => {
        setStatusFilter('open');
        setPriorityFilter('all');
        setDepartmentFilter('all');
        setAssigneeFilter('all');
        setTagFilter('all');
        setTicketNumberSearch('');
        setGeneralSearch('');
    };

    const hasActiveFilters = statusFilter !== 'open' || priorityFilter !== 'all' ||
        departmentFilter !== 'all' || assigneeFilter !== 'all' || tagFilter !== 'all' ||
        ticketNumberSearch || generalSearch;

    return (
        <div className="ticket-list">
            <div className="ticket-tabs">
                <div style={{ display: 'flex', gap: 8 }}>
                    {availableTabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => {
                                setActiveTab(tab.id);
                                setShowDeleted(false);
                            }}
                            className={`ticket-tab-btn ${activeTab === tab.id && !showDeleted ? 'active' : ''}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                {canViewDeleted && (
                    <button
                        onClick={() => {
                            setShowDeleted(true);
                            if (isAdmin) setActiveTab('all');
                        }}
                        className={`ticket-tab-btn ${showDeleted ? 'active' : ''}`}
                        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, color: showDeleted ? 'var(--accent-danger)' : 'var(--text-muted)' }}
                    >
                        <Archive size={14} />
                        Deleted
                    </button>
                )}
            </div>

            {/* Search & Filter Bar */}
            <div className="ticket-filter-bar">
                {/* Search Row */}
                <div className="ticket-search-row">
                    {/* Ticket # Search */}
                    <div className="ticket-search-group" style={{ flex: '0 0 120px' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>#</span>
                        <input
                            type="text"
                            className="ticket-search-input"
                            placeholder="Ticket #"
                            value={ticketNumberSearch}
                            onChange={(e) => setTicketNumberSearch(e.target.value)}
                            style={{ width: '80px' }}
                        />
                    </div>

                    {/* General Search */}
                    <div className="ticket-search-group" style={{ flex: 1, minWidth: '200px' }}>
                        <Search size={16} style={{ color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            className="ticket-search-input"
                            placeholder="Search tickets..."
                            value={generalSearch}
                            onChange={(e) => setGeneralSearch(e.target.value)}
                            style={{ flex: 1 }}
                        />
                    </div>

                    {/* Filter Toggle */}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`ticket-filter-toggle ${hasActiveFilters ? 'active' : ''}`}
                    >
                        <Filter size={16} />
                        Filters
                        {hasActiveFilters && (
                            <span style={{
                                background: 'rgba(255,255,255,0.3)',
                                borderRadius: '50%',
                                width: 18,
                                height: 18,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 11
                            }}>!</span>
                        )}
                    </button>
                </div>

                {/* Filter Dropdowns */}
                {showFilters && (
                    <div className="ticket-filter-grid">
                        {/* Status */}
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="input"
                            style={{ fontSize: 'var(--font-size-sm)' }}
                        >
                            <option value="all">All Status</option>
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                        </select>

                        {/* Priority */}
                        <select
                            value={priorityFilter}
                            onChange={(e) => setPriorityFilter(e.target.value)}
                            className="input"
                            style={{ fontSize: 'var(--font-size-sm)' }}
                        >
                            <option value="all">All Priority</option>
                            <option value="urgent">Urgent</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>

                        {/* Department */}
                        <select
                            value={departmentFilter}
                            onChange={(e) => setDepartmentFilter(e.target.value)}
                            className="input"
                            style={{ fontSize: 'var(--font-size-sm)' }}
                        >
                            <option value="all">All Departments</option>
                            {departments.map(d => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                        </select>

                        {/* Assignee */}
                        <select
                            value={assigneeFilter}
                            onChange={(e) => setAssigneeFilter(e.target.value)}
                            className="input"
                            style={{ fontSize: 'var(--font-size-sm)' }}
                        >
                            <option value="all">All Assignees</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                        </select>

                        {/* Tags */}
                        {availableTags.length > 0 && (
                            <select
                                value={tagFilter}
                                onChange={(e) => setTagFilter(e.target.value)}
                                className="input"
                                style={{ fontSize: 'var(--font-size-sm)' }}
                            >
                                <option value="all">All Tags</option>
                                {availableTags.map(tag => (
                                    <option key={tag} value={tag}>{tag}</option>
                                ))}
                            </select>
                        )}

                        {/* Clear Filters */}
                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="btn btn-danger btn-sm"
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                            >
                                <X size={14} />
                                Clear
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Create Button */}
            <button
                className="btn btn-primary"
                onClick={() => setShowCreateTicket(true)}
                style={{ marginBottom: 'var(--spacing-md)', width: '100%', gap: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Plus size={18} strokeWidth={2} />
                Create Ticket
            </button>

            {/* Results Count */}
            <div style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                marginBottom: 'var(--spacing-sm)'
            }}>
                {filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}
            </div>

            {/* Ticket Cards */}
            {filteredTickets.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <ClipboardList size={48} strokeWidth={1} opacity={0.3} />
                    </div>
                    <h3 className="empty-state-title">No tickets found</h3>
                    <p className="empty-state-text">
                        {hasActiveFilters
                            ? 'Try adjusting your filters'
                            : 'Create your first ticket to get started!'}
                    </p>
                </div>
            ) : (
                filteredTickets.map(ticket => (
                    <div
                        key={ticket.id}
                        className="ticket-card"
                        onClick={() => setActiveView({ type: 'ticket', id: ticket.id })}
                        style={{
                            borderLeft: isAssignedToMe(ticket)
                                ? '3px solid var(--accent-primary)'
                                : isCollaborator(ticket)
                                    ? '3px dotted var(--accent-secondary)'
                                    : '3px solid transparent',
                            background: isOverdue(ticket) ? 'rgba(224, 30, 90, 0.05)' : undefined
                        }}
                    >
                        <div className="ticket-card-header">
                            <span className="ticket-title">{ticket.title}</span>
                            <span className="ticket-id" style={{
                                fontWeight: 600,
                                color: 'var(--accent-primary)'
                            }}>
                                #{ticket.ticket_number || ticket.id.slice(0, 4)}
                            </span>
                        </div>

                        {ticket.description && (
                            <p className="ticket-description">{ticket.description}</p>
                        )}

                        {/* Tags */}
                        {ticket.tags && ticket.tags.length > 0 && (
                            <div style={{
                                display: 'flex',
                                gap: 'var(--spacing-xs)',
                                flexWrap: 'wrap',
                                marginBottom: 'var(--spacing-sm)'
                            }}>
                                {ticket.tags.map(tag => (
                                    <span key={tag} style={{
                                        fontSize: 10,
                                        padding: '2px 6px',
                                        background: 'var(--bg-tertiary)',
                                        borderRadius: 'var(--radius-sm)',
                                        color: 'var(--text-muted)'
                                    }}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="ticket-meta">
                            <span className={`status-badge ${ticket.status.replace('_', '-')}`}>
                                {ticket.status.replace('_', ' ')}
                            </span>

                            <span className={`priority-badge ${ticket.priority}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {getPriorityIcon(ticket.priority)}
                                {ticket.priority}
                            </span>

                            {ticket.department && (
                                <span style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: ticket.department.color,
                                    fontWeight: 500
                                }}>
                                    {ticket.department.name}
                                </span>
                            )}

                            {/* Due Date */}
                            {ticket.due_date && (
                                <span style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: isOverdue(ticket) ? 'var(--accent-danger)' : 'var(--text-muted)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4
                                }}>
                                    <CalendarDays size={12} />
                                    {formatDate(ticket.due_date)}
                                </span>
                            )}

                            <span style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--text-muted)',
                                marginLeft: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}>
                                <Calendar size={12} strokeWidth={1.5} />
                                {formatDate(ticket.created_at)}
                            </span>

                            {ticket.assignees && ticket.assignees.length > 0 && (
                                <div className="ticket-assignees">
                                    {ticket.assignees.slice(0, 3).map(assignee => (
                                        <div key={assignee.id} className="ticket-assignee" title={assignee.name}>
                                            {getInitials(assignee.name)}
                                        </div>
                                    ))}
                                    {ticket.assignees.length > 3 && (
                                        <div className="ticket-assignee">+{ticket.assignees.length - 3}</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}
